import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, ZoomIn, ZoomOut, Check } from "lucide-react";
import { getCroppedImg, type Area } from "../utils/cropImage";
import { useLanguage } from "../../context/LanguageContext";

interface Props {
  imageSrc: string;
  aspect: number;
  fileName?: string;
  mimeType?: string;
  title?: string;
  onCrop: (file: File) => void;
  onClose: () => void;
}

export default function ImageCropModal({
  imageSrc,
  aspect,
  fileName = "cropped.jpg",
  mimeType = "image/jpeg",
  title,
  onCrop,
  onClose,
}: Props) {
  const { t } = useLanguage();
  const resolvedTitle = title ?? t("imageCropModal.defaultTitle");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const file = await getCroppedImg(imageSrc, croppedAreaPixels, fileName, mimeType);
      onCrop(file);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-base">{resolvedTitle}</h3>
          <button onClick={onClose} disabled={processing}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-40">
            <X size={18} />
          </button>
        </div>

        {/* Crop area */}
        <div className="relative bg-black" style={{ height: 380 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid
            style={{
              containerStyle: { borderRadius: 0 },
              cropAreaStyle: { border: "2px solid #b3d130", color: "rgba(0,0,0,0.55)" },
            }}
          />
        </div>

        {/* Zoom control */}
        <div className="px-5 py-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <button onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
              className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors">
              <ZoomOut size={16} />
            </button>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full appearance-none bg-gray-200 accent-[#b3d130] cursor-pointer"
            />
            <button onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
              className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors">
              <ZoomIn size={16} />
            </button>
            <span className="text-xs text-gray-400 w-10 text-right">{Math.round(zoom * 100)}%</span>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">{t("imageCropModal.repositionHint")}</p>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-5 pb-5">
          <button onClick={onClose} disabled={processing}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40">
            {t("common.cancel")}
          </button>
          <button onClick={handleSave} disabled={processing}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#b3d130] to-[#a5c428] rounded-xl text-sm font-bold text-[#313131] hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {processing
              ? <span className="w-4 h-4 border-2 border-[#313131] border-t-transparent rounded-full animate-spin" />
              : <Check size={16} />}
            {processing ? t("imageCropModal.processing") : t("imageCropModal.applyCrop")}
          </button>
        </div>
      </div>
    </div>
  );
}
